import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1100, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const RAW_BASE = process.env.REACT_APP_API_BASE || '/api';
const API_BASE = RAW_BASE.replace(/["']/g, '').replace(/\/+$/, '');

// CEFR band order for consistent sorting
const BAND_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export default function ExamAttemptDetails() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState([]);
  const [examConfig, setExamConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAttemptDetails();
  }, [assignmentId]);

  const fetchAttemptDetails = async () => {
    setLoading(true); setError('');
    try {
      console.log(`🔍 Fetching attempt details for assignmentId: ${assignmentId}`);
      
      // Fetch answers
      const res = await fetch(`${API_BASE}/admin/review/attempts/${assignmentId}/details`);
      console.log(`📥 Attempt details response status: ${res.status}`);
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server responded with ${res.status}`);
      }
      
      const data = await res.json();
      console.log('📦 Attempt data:', data);
      setAnswers(data);

      // Fetch exam config for point values
      if (data.length > 0 && data[0].exam_id) {
        const examRes = await fetch(`${API_BASE}/admin/exams/${data[0].exam_id}`);
        if (examRes.ok) {
          const examData = await examRes.json();
          setExamConfig(examData);
        }
      }
    } catch (err) {
      console.error('❌ Fetch attempt error:', err);
      setError(err.message);
    } finally { setLoading(false); }
  };

  if (loading) return <div style={{...containerStyle, textAlign: 'center', marginTop: 60}}>⏳ Loading attempt details...</div>;
  if (error) return <div style={{...containerStyle, padding: 20, color: '#721c24', background: '#f8d7da', borderRadius: 8}}>❌ {error}<br/><button onClick={() => navigate(-1)} style={{...btnSecondary, marginTop: 10}}>← Back</button></div>;
  if (answers.length === 0) return <div style={{...containerStyle, textAlign: 'center', padding: 40}}>No answer records found for this attempt.</div>;

  // ✅ Extract exam config values with fallbacks
  const primaryPoints = examConfig?.primary_points ?? 1;
  const backupPoints = examConfig?.backup_points ?? 0.5;

  // ✅ Aggregate stats by Band → Tags
  const bandStats = {};
  answers.forEach(ans => {
    const band = ans.band || 'Unknown';
    if (!bandStats[band]) {
      bandStats[band] = { 
        tags: {}, 
        totalCorrect: 0, 
        totalWrong: 0,
        weightedEarned: 0,
        weightedMax: 0
      };
    }
    
    // Determine if this was a primary or backup question
    const isPrimary = ans.is_primary !== undefined ? ans.is_primary : true;
    const points = isPrimary ? primaryPoints : backupPoints;
    
    // Update band totals
    bandStats[band].weightedMax += points;
    if (ans.is_correct) {
      bandStats[band].totalCorrect++;
      bandStats[band].weightedEarned += points;
    } else {
      bandStats[band].totalWrong++;
    }
    
    // Update tag stats within band
    ans.tags?.forEach(tag => {
      if (!tag || tag.trim() === '') return;
      if (!bandStats[band].tags[tag]) {
        bandStats[band].tags[tag] = { correct: 0, wrong: 0 };
      }
      if (ans.is_correct) {
        bandStats[band].tags[tag].correct++;
      } else {
        bandStats[band].tags[tag].wrong++;
      }
    });
  });

  // ✅ Sort bands in CEFR order
  const sortedBands = Object.keys(bandStats).sort((a, b) => {
    const idxA = BAND_ORDER.indexOf(a);
    const idxB = BAND_ORDER.indexOf(b);
    if (idxA === -1 && idxB === -1) return a.localeCompare(b);
    if (idxA === -1) return 1;
    if (idxB === -1) return -1;
    return idxA - idxB;
  });

  // ✅ Calculate overall weighted score
  const overallWeightedMax = Object.values(bandStats).reduce((sum, b) => sum + b.weightedMax, 0);
  const overallWeightedEarned = Object.values(bandStats).reduce((sum, b) => sum + b.weightedEarned, 0);
  const overallPercentage = overallWeightedMax > 0 ? Math.round((overallWeightedEarned / overallWeightedMax) * 100) : 0;

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Attempt Review</h2>
        <button onClick={() => navigate(-1)} style={btnSecondary}>← Back to Results</button>
      </div>

      {/* Overall Summary Banner */}
      <div style={{ display: 'flex', justifyContent: 'space-around', background: '#fff', padding: 15, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <div style={{ textAlign: 'center' }}><strong style={{ fontSize: 20, color: '#28a745' }}>{overallPercentage}%</strong><br/><span style={{ fontSize: 12, color: '#666' }}>Weighted Score</span></div>
        <div style={{ textAlign: 'center' }}><strong style={{ fontSize: 20, color: '#007BFF' }}>{overallWeightedEarned.toFixed(1)}/{overallWeightedMax.toFixed(1)}</strong><br/><span style={{ fontSize: 12, color: '#666' }}>Points Earned</span></div>
        //<div style={{ textAlign: 'center' }}><strong style={{ fontSize: 20, color: '#6c757d' }}>{answers.length}</strong><br/><span style={{ fontSize: 12, color: '#666' }}>Total Questions</span></div>
      </div>

      {/* ✅ NEW: Band Summary Table (Weighted Scoring) */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #ddd', overflow: 'hidden', marginBottom: 25 }}>
        <div style={{ padding: '12px 15px', background: '#e9ecef', fontWeight: 600, fontSize: 14, borderBottom: '1px solid #dee2e6' }}>
          📊 Band Performance Summary (Weighted by Question Type)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.2fr 1fr', gap: 10, padding: '10px 15px', background: '#f8f9fa', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #eee' }}>
          <div>Band</div>
          <div style={{ textAlign: 'center', color: '#28a745' }}>Correct</div>
          <div style={{ textAlign: 'center', color: '#dc3545' }}>Wrong</div>
          <div style={{ textAlign: 'center' }}>Weighted Score</div>
          <div style={{ textAlign: 'center' }}>Accuracy</div>
        </div>
        {sortedBands.map(band => {
          const stats = bandStats[band];
          const totalQ = stats.totalCorrect + stats.totalWrong;
          const accuracy = totalQ > 0 ? Math.round((stats.totalCorrect / totalQ) * 100) : 0;
          
          return (
            <div key={band} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.2fr 1fr', gap: 10, padding: '10px 15px', borderBottom: '1px solid #f0f0f0', alignItems: 'center', fontSize: 14 }}>
              <div style={{ fontWeight: 600, color: '#333' }}>{band}</div>
              <div style={{ textAlign: 'center', fontWeight: 600, color: '#28a745' }}>{stats.totalCorrect}</div>
              <div style={{ textAlign: 'center', fontWeight: 600, color: '#dc3545' }}>{stats.totalWrong}</div>
              <div style={{ textAlign: 'center' }}>
                <strong style={{ color: '#007BFF', fontSize: 15 }}>{stats.weightedEarned.toFixed(1)} / {stats.weightedMax.toFixed(1)} pts</strong>
              </div>
              <div style={{ textAlign: 'center', fontWeight: 600 }}>
                <span style={{ 
                  padding: '3px 10px', borderRadius: 4, fontSize: 12,
                  background: accuracy >= 70 ? '#d4edda' : accuracy >= 40 ? '#fff3cd' : '#f8d7da',
                  color: accuracy >= 70 ? '#155724' : accuracy >= 40 ? '#856404' : '#721c24'
                }}>
                  {accuracy}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ✅ UPDATED: Tag Breakdown by Band (with percentages) */}
      {sortedBands.map(band => {
        const stats = bandStats[band];
        const tagEntries = Object.entries(stats.tags).sort(([a], [b]) => a.localeCompare(b));
        
        return (
          <div key={band} style={{ background: '#fff', borderRadius: 8, border: '1px solid #ddd', marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ padding: '12px 15px', background: '#007BFF', color: '#fff', fontWeight: 600, fontSize: 15 }}>
              🎯 Band {band} — Tag Performance
            </div>
            
            {tagEntries.length === 0 ? (
              <div style={{ padding: 15, color: '#666', textAlign: 'center' }}>No tag data available for this band.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, padding: '10px 15px', background: '#f8f9fa', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #eee' }}>
                <div>Tag</div>
                <div style={{ textAlign: 'center', color: '#28a745' }}>Correct</div>
                <div style={{ textAlign: 'center', color: '#dc3545' }}>Wrong</div>
                <div style={{ textAlign: 'center' }}>Accuracy</div>
              </div>
            )}
            
            {tagEntries.map(([tag, data]) => {
              const total = data.correct + data.wrong;
              const correctPct = total > 0 ? Math.round((data.correct / total) * 100) : 0;
              const wrongPct = total > 0 ? Math.round((data.wrong / total) * 100) : 0;
              
              return (
                <div key={tag} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, padding: '10px 15px', borderBottom: '1px solid #f0f0f0', alignItems: 'center', fontSize: 14 }}>
                  <div style={{ fontWeight: 600 }}>
                    <span style={{ padding: '2px 8px', background: '#e2e3e5', borderRadius: 4, fontSize: 12 }}>{tag}</span>
                  </div>
                  <div style={{ textAlign: 'center', color: '#28a745' }}>
                    <strong>{data.correct}</strong> <span style={{ fontSize: 12, color: '#666' }}>({correctPct}%)</span>
                  </div>
                  <div style={{ textAlign: 'center', color: '#dc3545' }}>
                    <strong>{data.wrong}</strong> <span style={{ fontSize: 12, color: '#666' }}>({wrongPct}%)</span>
                  </div>
                  <div style={{ textAlign: 'center', fontWeight: 600 }}>
                    <span style={{ 
                      padding: '3px 10px', borderRadius: 4, fontSize: 12,
                      background: correctPct >= 70 ? '#d4edda' : correctPct >= 40 ? '#fff3cd' : '#f8d7da',
                      color: correctPct >= 70 ? '#155724' : correctPct >= 40 ? '#856404' : '#721c24'
                    }}>
                      {correctPct}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Question List (unchanged) */}
      <h3 style={{ marginBottom: 15, marginTop: 30 }}>Question-by-Question Review</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {answers.map((ans, idx) => {
          const optionKeys = ['A', 'B', 'C', 'D'];
          return (
            <div key={ans.id} style={{ 
              background: '#fff', borderRadius: 8, border: `1px solid ${ans.is_correct ? '#28a745' : '#dc3545'}`, 
              borderLeft: `6px solid ${ans.is_correct ? '#28a745' : '#dc3545'}`, padding: 18 
            }}>
              {/* Question Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <strong style={{ fontSize: 15, color: '#333' }}>Question {idx + 1}</strong>
                  <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>Band: {ans.band}</span>
                  {ans.tags && ans.tags.length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                      {ans.tags.map(tag => (
                        <span key={tag} style={{ 
                          padding: '2px 6px', background: '#e2e3e5', borderRadius: 4, 
                          fontSize: 10, color: '#383d41', fontWeight: 600 
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <span style={{ 
                  padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                  background: ans.is_correct ? '#d4edda' : '#f8d7da',
                  color: ans.is_correct ? '#155724' : '#721c24'
                }}>
                  {ans.is_correct ? '✅ Correct' : '❌ Incorrect'}
                </span>
              </div>

              {/* Question Text */}
              <p style={{ margin: '0 0 12px 0', fontSize: 14, lineHeight: 1.4, color: '#333' }}>
                {ans.question_text}
              </p>

              {/* Answer Options */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                {optionKeys.map(opt => {
                  const text = ans.options?.[opt];
                  if (!text) return null;
                  
                  const isCorrect = opt === ans.correct_answer;
                  const isSelected = opt === ans.selected_answer;
                  
                  let bg = '#f8f9fa';
                  let border = '1px solid #dee2e6';
                  let color = '#333';
                  
                  if (isSelected && isCorrect) {
                    bg = '#d4edda'; border = '2px solid #28a745'; color = '#155724';
                  } else if (isSelected && !isCorrect) {
                    bg = '#f8d7da'; border = '2px solid #dc3545'; color = '#721c24';
                  } else if (isCorrect) {
                    bg = '#cce5ff'; border = '1px solid #007BFF'; color = '#004085';
                  }
                  
                  return (
                    <div key={opt} style={{ 
                      padding: '8px 10px', borderRadius: 6, background: bg, border, color,
                      fontSize: 13, display: 'flex', alignItems: 'center', gap: 6
                    }}>
                      <strong style={{ minWidth: 18 }}>{opt}.</strong>
                      <span>{text}</span>
                      {isSelected && <span style={{ marginLeft: 'auto', fontSize: 11 }}>← Student's answer</span>}
                      {isCorrect && !isSelected && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#007BFF' }}>✓ Correct</span>}
                    </div>
                  );
                })}
              </div>

              {/* Answer Summary */}
              <div style={{ fontSize: 12, color: '#666', borderTop: '1px dashed #eee', paddingTop: 10 }}>
                <strong>Your answer:</strong> {ans.selected_answer} &nbsp;|&nbsp; 
                <strong>Correct answer:</strong> {ans.correct_answer} &nbsp;|&nbsp;
                <strong>Time:</strong> {new Date(ans.answered_at).toLocaleTimeString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}